import React, { useState, useEffect } from 'react';
import { Standard } from '../../../data/types';
import { GameIcons } from '../../styles/GameIcons';
import '../../styles/GameStyleUI.css';

interface LearningPhaseVideoProps {
  standard: Standard | null;
  onComplete: () => void;
}

const LearningPhaseVideo: React.FC<LearningPhaseVideoProps> = ({ standard, onComplete }) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);

  // Simulate video progress
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isPlaying && videoProgress < 100) {
      interval = setInterval(() => {
        setVideoProgress(prev => {
          const newProgress = prev + 1;
          if (newProgress >= 100) {
            setVideoCompleted(true);
            clearInterval(interval);
          }
          return newProgress;
        });
      }, 300); // Update every 300ms for a 30-second simulated video
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, videoProgress]);

  // Simulate video loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setVideoLoaded(true);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  const handleTogglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSkipVideo = () => {
    setVideoProgress(100);
    setVideoCompleted(true);
  };

  return (
    <div className="bg-gradient-to-b from-indigo-50 to-blue-50 p-6 rounded-xl">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center">
          <GameIcons.Video className="mr-2" size={24} color="#4F46E5" />
          <span>Learn with Video</span>
        </h2>
        {videoProgress > 0 && (
          <div className="flex items-center">
            <span className="text-indigo-700 font-medium mr-2">{videoProgress}%</span>
            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-500 rounded-full"
                style={{ width: `${videoProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Video title and standard info */}
      <div className="bg-white shadow-sm rounded-lg p-4 mb-4">
        <h3 className="font-bold text-lg text-gray-800">{standard?.title || 'Math Concept Video'}</h3>
        <p className="text-gray-600 text-sm">
          {standard?.description || 'Watch this video to learn about the concept'}
        </p>
        <div className="flex items-center mt-2">
          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
            {standard?.code || 'Standard Code'}
          </span>
          <span className="ml-2 text-gray-500 text-xs">Estimated time: 3-5 minutes</span>
        </div>
      </div>
      
      {/* Video player */}
      <div className="relative bg-black rounded-lg overflow-hidden shadow-xl aspect-video mb-6">
        {!videoLoaded ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid"></div>
          </div>
        ) : (
          <>
            {/* Video placeholder with simulated content */}
            <div className="relative w-full h-full bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
              <div className="text-center">
                <GameIcons.PlayCircle size={64} className="mx-auto mb-3" color="#FFFFFF" />
                <p className="text-white text-lg">
                  {standard?.title || 'Learning the Concept'} Video
                </p>
              </div>
            </div>
            
            {/* Video controls */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="flex flex-col">
                {/* Progress bar */}
                <div className="relative w-full h-2 bg-gray-700 rounded-full mb-3 cursor-pointer">
                  <div 
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${videoProgress}%` }}
                  ></div>
                  <div 
                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-md"
                    style={{ left: `calc(${videoProgress}% - 8px)` }}
                  ></div>
                </div>
                
                {/* Controls */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <button 
                      onClick={handleTogglePlay}
                      className="text-white hover:text-blue-400 transition-colors mr-4"
                    >
                      {isPlaying ? 
                        <GameIcons.Pause size={24} color="currentColor" /> : 
                        <GameIcons.Play size={24} color="currentColor" />
                      }
                    </button>
                    <div className="text-white text-sm">
                      {Math.floor(videoProgress * 0.3 / 60)}:{String(Math.floor(videoProgress * 0.3 % 60)).padStart(2, '0')} / 
                      {' 3:00'}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button className="text-white hover:text-blue-400 transition-colors mr-3">
                      <GameIcons.ClosedCaption size={20} color="currentColor" />
                    </button>
                    <button className="text-white hover:text-blue-400 transition-colors">
                      <GameIcons.FullScreen size={20} color="currentColor" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex justify-between items-center">
        <button
          onClick={handleSkipVideo}
          className="text-indigo-600 hover:text-indigo-800 transition-colors font-medium flex items-center"
        >
          <GameIcons.FastForward size={20} className="mr-1" color="currentColor" />
          Skip Video
        </button>
        
        <button
          onClick={onComplete}
          disabled={!videoCompleted}
          className={`flex items-center px-6 py-3 rounded-lg shadow-md transition-colors ${
            videoCompleted 
              ? 'bg-gradient-to-r from-indigo-600 to-blue-500 hover:from-indigo-700 hover:to-blue-600 text-white' 
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <span>Next Step</span>
          <GameIcons.ArrowRight size={18} className="ml-2" color="currentColor" />
        </button>
      </div>
      
      {/* Auto-save indicator */}
      <div className="mt-4 text-center">
        <span className="inline-flex items-center text-xs text-green-600">
          <GameIcons.CloudCheck size={14} className="mr-1" color="currentColor" />
          Your progress is being saved automatically
        </span>
      </div>
    </div>
  );
};

export default LearningPhaseVideo;
